import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  Users,
  FolderOpen,
  DollarSign,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  Clock,
  Activity,
  Settings,
  Shield,
  BarChart3,
  UserCheck,
} from 'lucide-react';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { fetchPaymentRequests } from '../../services/PaymentService';

interface AdminStats {
  totalUsers: number;
  totalProjects: number;
  totalPayments: number;
  pendingWithdrawals: number;
  activeUsers: number;
  completedProjects: number;
  systemHealth: 'good' | 'warning' | 'error';
}
interface Review {
  _id: string;
  user: {
    _id: string;
    firstName: string;
    lastName: string;
    profileImage?: string;
    role: string;
  };
  rating: number;
  title: string;
  content: string;
  projectType: string;
  helpfulCount: number;
  helpfulBy?: string[];
  createdAt: string;
  featured: boolean;
}


interface RecentActivity {
  _id: string;
  type:
    | 'user_registered'
    | 'project_created'
    | 'payment_processed'
    | 'withdrawal_requested';
  description: string;
  timestamp: string;
  user?: string;
}

const getApiBaseUrl = (): string => {
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    const protocol = window.location.protocol;

    // Special handling for WebContainer preview URLs
    if (hostname.includes('webcontainer.io') || hostname.includes('webcontainer-api.io')) {
      const baseUrl = hostname.replace('--5173--', '--5002--');
      return `${protocol}//${baseUrl}/api`;
    }

    // Local development
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return `${protocol}//${hostname}:5002/api`;
    }

    // Render or other deployment domains
    return `${protocol}//${hostname}/api`;
  }

  // Fallback to env or default
  return import.meta.env.VITE_API_URL || 'http://localhost:5002/api';
};

const API_BASE_URL = getApiBaseUrl();

const AdminDashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState<AdminStats>({
    totalUsers: 0,
    totalProjects: 0,
    totalPayments: 0,
    pendingWithdrawals: 0,
    activeUsers: 0,
    completedProjects: 0,
    systemHealth: 'good',
  });
  const [recentActivity, setRecentActivity] = useState<RecentActivity[]>([]);
  const [chartData, setChartData] = useState<{
    userPie: { name: string; value: number }[];
    projectBar: { name: string; value: number }[];
  }>({ userPie: [], projectBar: [] });

  const [loading, setLoading] = useState(true);
  const [pendingReviews, setPendingReviews] = useState<Review[]>([]);

  const [adminPaymentStats, setAdminPaymentStats] = useState({
    total: 0,
    paid: 0,
    pending: 0,
    overdue: 0,
  });

  // Redirect based on user role
  useEffect(() => {
    if (user && user.role !== 'admin') {
      if (user.role === 'client') {
        navigate('/dashboard/client', { replace: true });
      } else if (user.role === 'developer') {
        navigate('/dashboard/developer', { replace: true });
      }
    }
  }, [user, navigate]);

  useEffect(() => {
    if (user && user.role === 'admin') {
      fetchAdminData();
    }
  }, [user]);

  const fetchAdminData = async () => {
    try {
      const token = localStorage.getItem('accessToken');

      // Fetch all stats using available endpoints
      const [usersRes, projectsRes, paymentsRes, withdrawalsRes] =
        await Promise.all([
          fetch(`${API_BASE_URL}/users`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          fetch(`${API_BASE_URL}/projects`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          fetch(`${API_BASE_URL}/payments/admin`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          fetch(`${API_BASE_URL}/withdrawals`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);

      const [usersData, projectsData, paymentsData, withdrawalsData] =
        await Promise.all([
          usersRes.json(),
          projectsRes.json(),
          paymentsRes.json(),
          withdrawalsRes.json(),
        ]);

      const [pendingReviewsRes] = await Promise.all([
        fetch(`${API_BASE_URL}/reviews/pending`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      const { reviews } = await pendingReviewsRes.json();
      setPendingReviews(reviews || []);

      // Calculate stats from available data
      const totalUsers = usersData.users?.length || 0;
      const activeUsers =
        usersData.users?.filter((u: any) => u.isOnline).length || 0;
      const totalProjects = projectsData.projects?.length || 0;
      const completedProjects =
        projectsData.projects?.filter((p: any) => p.status === 'completed')
          .length || 0;
      const totalPayments =
        paymentsData.payments?.reduce(
          (sum: number, p: any) => sum + p.amount,
          0
        ) || 0;
      const pendingWithdrawals =
        withdrawalsData.withdrawals?.filter((w: any) => w.status === 'pending')
          .length || 0;

      // Calculate payment stats from payments data
      const paymentStats = {
        total: paymentsData.payments?.length || 0,
        paid:
          paymentsData.payments?.filter((p: any) => p.status === 'completed')
            .length || 0,
        pending:
          paymentsData.payments?.filter((p: any) => p.status === 'pending')
            .length || 0,
        overdue: 0, // This would need to be calculated based on due dates if available
      };

      setStats({
        totalUsers,
        totalProjects,
        totalPayments,
        pendingWithdrawals,
        activeUsers,
        completedProjects,
        systemHealth: 'good',
      });

      setAdminPaymentStats(paymentStats);

      // Prepare chart data
      const userPieData = [
        { name: 'Active Users', value: activeUsers },
        { name: 'Inactive Users', value: totalUsers - activeUsers },
      ];

      const projectBarData = [
        { name: 'Completed', value: completedProjects },
        { name: 'Pending', value: totalProjects - completedProjects },
      ];

      setChartData({
        userPie: userPieData,
        projectBar: projectBarData,
      });

      // Create recent activity from available data
      const activities: RecentActivity[] = [];

      // Add user registrations
      if (usersData.users) {
        const recentUsers = usersData.users
          .sort(
            (a: any, b: any) =>
              new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
          )
          .slice(0, 2);

        recentUsers.forEach((user: any) => {
          activities.push({
            _id: user._id,
            type: 'user_registered',
            description: `New ${user.role} registered: ${user.firstName} ${user.lastName}`,
            timestamp: user.createdAt,
            user: `${user.firstName} ${user.lastName}`,
          });
        });
      }

      // Add recent projects
      if (projectsData.projects) {
        const recentProjects = projectsData.projects
          .sort(
            (a: any, b: any) =>
              new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
          )
          .slice(0, 2);

        recentProjects.forEach((project: any) => {
          activities.push({
            _id: project._id,
            type: 'project_created',
            description: `New project created: ${project.title}`,
            timestamp: project.createdAt,
            user: project.client?.firstName + ' ' + project.client?.lastName,
          });
        });
      }

      // Add recent payments
      if (paymentsData.payments) {
        const recentPayments = paymentsData.payments
          .sort(
            (a: any, b: any) =>
              new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
          )
          .slice(0, 2);

        recentPayments.forEach((payment: any) => {
          activities.push({
            _id: payment._id,
            type: 'payment_processed',
            description: `Payment processed: ${formatCurrency(
              payment.amount,
              payment.currency
            )} for ${payment.description}`,
            timestamp: payment.createdAt,
            user: payment.client?.firstName + ' ' + payment.client?.lastName,
          });
        });
      }

      // Add recent withdrawals
      if (withdrawalsData.withdrawals) {
        const recentWithdrawals = withdrawalsData.withdrawals
          .sort(
            (a: any, b: any) =>
              new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
          )
          .slice(0, 2);

        recentWithdrawals.forEach((withdrawal: any) => {
          activities.push({
            _id: withdrawal._id,
            type: 'withdrawal_requested',
            description: `Withdrawal requested: ${formatCurrency(
              withdrawal.amount,
              withdrawal.currency
            )}`,
            timestamp: withdrawal.createdAt,
            user: withdrawal.user?.firstName + ' ' + withdrawal.user?.lastName,
          });
        });
      }

      // Sort activities by timestamp and take the most recent ones
      setRecentActivity(
        activities
          .sort(
            (a, b) =>
              new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
          )
          .slice(0, 4)
      );
    } catch (error) {
      setStats((prev) => ({ ...prev, systemHealth: 'warning' }));
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
    }).format(amount);
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'user_registered':
        return <UserCheck className="w-4 h-4 text-green-600" />;
      case 'project_created':
        return <FolderOpen className="w-4 h-4 text-blue-600" />;
      case 'payment_processed':
        return <DollarSign className="w-4 h-4 text-green-600" />;
      case 'withdrawal_requested':
        return <AlertCircle className="w-4 h-4 text-orange-600" />;
      default:
        return <Activity className="w-4 h-4 text-gray-600" />;
    }
  };

  const handleApproveReview = async (reviewId: string) => {
    try {
      const token = localStorage.getItem('accessToken');
      const res = await fetch(`${API_BASE_URL}/reviews/approve/${reviewId}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      const data = await res.json();
      if (res.ok) {
        setPendingReviews((prev) => prev.filter((r) => r._id !== reviewId));
      } else {
      }
    } catch (err) {
    }
  };

  const getRelativeTime = (timestamp: string) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diffMs = now.getTime() - time.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  const getSystemHealthColor = (health: string) => {
    switch (health) {
      case 'good':
        return 'text-green-600 bg-green-100';
      case 'warning':
        return 'text-yellow-600 bg-yellow-100';
      case 'error':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-16 bg-gray-200 rounded-lg animate-pulse"></div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(4)].map((_, index) => (
            <div
              key={index}
              className="bg-white p-6 rounded-lg shadow-sm border animate-pulse"
            >
              <div className="h-4 bg-gray-200 rounded w-20 mb-4"></div>
              <div className="h-8 bg-gray-200 rounded w-16 mb-2"></div>
              <div className="h-3 bg-gray-200 rounded w-24"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
          <p className="text-gray-600 mt-1">
            System overview and management tools
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <span
            className={`px-3 py-1 text-sm font-medium rounded-full capitalize ${getSystemHealthColor(
              stats.systemHealth
            )}`}
          >
            System {stats.systemHealth}
          </span>
          <Link
            to="/dashboard/admin/settings"
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200"
          >
            <Settings className="w-4 h-4 mr-2" />
            Settings
          </Link>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Users</p>
              <p className="text-2xl font-bold text-gray-900">
                {stats.totalUsers}
              </p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <Users className="w-6 h-6 text-blue-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm">
            <TrendingUp className="w-4 h-4 text-green-500 mr-1" />
            <span className="text-green-600">{stats.activeUsers} active</span>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">
                Total Projects
              </p>
              <p className="text-2xl font-bold text-gray-900">
                {stats.totalProjects}
              </p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <FolderOpen className="w-6 h-6 text-green-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm">
            <CheckCircle className="w-4 h-4 text-green-500 mr-1" />
            <span className="text-gray-600">
              {stats.completedProjects} completed
            </span>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">
                Total Payments
              </p>
              <p className="text-2xl font-bold text-gray-900">
                {formatCurrency(stats.totalPayments)}
              </p>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-purple-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm">
            <TrendingUp className="w-4 h-4 text-green-500 mr-1" />
            <span className="text-green-600">+8% this month</span>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">
                Pending Withdrawals
              </p>
              <p className="text-2xl font-bold text-gray-900">
                {stats.pendingWithdrawals}
              </p>
            </div>
            <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
              <Clock className="w-6 h-6 text-orange-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm">
            <AlertCircle className="w-4 h-4 text-orange-500 mr-1" />
            <span className="text-orange-600">Requires attention</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
        <div className="bg-white dark:bg-gray-800 shadow rounded p-4 text-center">
          <h4 className="text-sm text-gray-500">All Payments</h4>
          <p className="text-xl font-bold">{adminPaymentStats.total}</p>
        </div>
        <div className="bg-white dark:bg-gray-800 shadow rounded p-4 text-center">
          <h4 className="text-sm text-gray-500">Paid</h4>
          <p className="text-xl font-bold text-green-600">
            {adminPaymentStats.paid}
          </p>
        </div>
        <div className="bg-white dark:bg-gray-800 shadow rounded p-4 text-center">
          <h4 className="text-sm text-gray-500">Pending</h4>
          <p className="text-xl font-bold text-yellow-500">
            {adminPaymentStats.pending}
          </p>
        </div>
        <div className="bg-white dark:bg-gray-800 shadow rounded p-4 text-center">
          <h4 className="text-sm text-gray-500">Overdue</h4>
          <p className="text-xl font-bold text-red-500">
            {adminPaymentStats.overdue}
          </p>
        </div>
      </div>

      <div className="mt-4">
        <a
          href="/dashboard/admin/payment-requests"
          className="inline-block mt-2 px-4 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
        >
          Manage Payments
        </a>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Activity */}
        <div className="bg-white rounded-lg shadow-sm border">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              Recent Activity
            </h2>
          </div>

          <div className="p-6">
            <div className="space-y-4">
              {recentActivity.map((activity) => (
                <div key={activity._id} className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                    {getActivityIcon(activity.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-900">
                      {activity.description}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {getRelativeTime(activity.timestamp)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              Pending Reviews
            </h2>
          </div>

          <div className="p-6 space-y-4">
            {pendingReviews?.length === 0 ? (
              <p className="text-gray-600 text-sm">No pending reviews</p>
            ) : (
              pendingReviews?.map((review) => (
                <div key={review._id} className="border rounded-lg p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-medium text-gray-900">
                        {review.title}
                      </p>
                      <p className="text-sm text-gray-700 mt-1">
                        {review.content}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        By {review.user.firstName} {review.user.lastName}
                      </p>
                    </div>
                    <button
                      onClick={() => handleApproveReview(review._id)}
                      className="ml-4 px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700"
                    >
                      Approve
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-lg shadow-sm border">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              Quick Actions
            </h2>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-2 gap-4">
              <Link
                to="/dashboard/admin/users"
                className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors duration-200"
              >
                <Users className="w-8 h-8 text-blue-600 mb-2" />
                <h3 className="font-medium text-gray-900">User Management</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Manage user accounts and roles
                </p>
              </Link>

              <Link
                to="/dashboard/earnings/withdrawals"
                className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors duration-200"
              >
                <DollarSign className="w-8 h-8 text-green-600 mb-2" />
                <h3 className="font-medium text-gray-900">Withdrawals</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Review pending withdrawals
                </p>
              </Link>

              <Link
                to="/dashboard/projects"
                className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors duration-200"
              >
                <FolderOpen className="w-8 h-8 text-purple-600 mb-2" />
                <h3 className="font-medium text-gray-900">Projects</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Monitor all projects
                </p>
              </Link>

              <Link
                to="/dashboard/admin/settings"
                className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors duration-200"
              >
                <Settings className="w-8 h-8 text-gray-600 mb-2" />
                <h3 className="font-medium text-gray-900">System Settings</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Configure system settings
                </p>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* System Overview */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            System Overview
          </h2>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <BarChart3 className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">
                Performance
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                System running optimally
              </p>
              <div className="mt-3">
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-green-500 h-2 rounded-full"
                    style={{ width: '95%' }}
                  ></div>
                </div>
                <p className="text-xs text-gray-500 mt-1">95% uptime</p>
              </div>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Security</h3>
              <p className="text-sm text-gray-600 mt-1">All systems secure</p>
              <div className="mt-3">
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-green-500 h-2 rounded-full"
                    style={{ width: '100%' }}
                  ></div>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  No threats detected
                </p>
              </div>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Activity className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Activity</h3>
              <p className="text-sm text-gray-600 mt-1">High user engagement</p>
              <div className="mt-3">
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-purple-500 h-2 rounded-full"
                    style={{ width: '87%' }}
                  ></div>
                </div>
                <p className="text-xs text-gray-500 mt-1">87% active users</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Admin Charts */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            User & Project Overview
          </h2>
        </div>

        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Pie Chart for User Activity */}
          <div>
            <h3 className="text-md font-semibold mb-4 text-gray-800">
              User Activity
            </h3>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={chartData.userPie}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  label
                >
                  <Cell fill="#34d399" /> {/* Active - green */}
                  <Cell fill="#d1d5db" /> {/* Inactive - gray */}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Bar Chart for Project Status */}
          <div>
            <h3 className="text-md font-semibold mb-4 text-gray-800">
              Project Status
            </h3>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={chartData.projectBar}>
                <XAxis dataKey="name" />
                <YAxis allowDecimals={false} />
                <Tooltip formatter={(value) => [`${value}`, 'Count']} />
                <Bar dataKey="value">
                  {chartData.projectBar.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={entry.name === 'Completed' ? '#10b981' : '#3b82f6'}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
